// Phase 9 /models/calibration — Phase 7 calibration sessions.
//
// List view with filters + detail modal. Admin-only accept/reject.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { calibration as calibApi } from "@/api/endpoints";
import useUserRole from "@/hooks/useUserRole";
import { formatApiError } from "@/lib/formatApiError";

const STATUSES = ["", "proposed", "accepted", "rejected"];

export default function CalibrationSessionsPage() {
    const { isAdmin } = useUserRole();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("");
    const [logFilter, setLogFilter] = useState("");
    const [modelFilter, setModelFilter] = useState("");
    const [open, setOpen] = useState(null);

    const refresh = async () => {
        setLoading(true);
        try {
            const params = {};
            if (status) params.status = status;
            if (logFilter) params.log_import_id = logFilter;
            if (modelFilter) params.model_id = modelFilter;
            const data = await calibApi.list(params);
            setRows(Array.isArray(data) ? data : data?.items || []);
        } catch (e) {
            toast.error(formatApiError(e, "Failed to load sessions"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, logFilter, modelFilter]);

    const openDetail = async (s) => {
        try {
            const detail = await calibApi.get(s.id || s.session_id);
            setOpen(detail);
        } catch (e) {
            toast.error(formatApiError(e, "Load failed"));
        }
    };

    const decide = async (id, which) => {
        try {
            if (which === "accept") await calibApi.accept(id, {});
            else await calibApi.reject(id, {});
            toast.success(`Session ${which}ed`);
            setOpen(null);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e, "Decision failed"));
        }
    };

    const filtered = useMemo(() => rows, [rows]);

    return (
        <div data-testid="calibration-page">
            <PageHeader
                title="Calibration Sessions"
                subtitle="Propose, accept, or reject parameter deltas for registered models."
            />

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-ux-text-3 mb-1">
                        Status
                    </div>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="px-2 py-1.5 rounded-md bg-ux-bg-1 border border-ux-border-1 text-[13px] text-ux-text-1"
                        data-testid="calibration-filter-status"
                    >
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {s || "(any)"}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-ux-text-3 mb-1">
                        Log import id
                    </div>
                    <input
                        value={logFilter}
                        onChange={(e) => setLogFilter(e.target.value)}
                        placeholder="optional filter"
                        className="px-2 py-1.5 rounded-md bg-ux-bg-1 border border-ux-border-1 text-[13px] text-ux-text-1"
                        data-testid="calibration-filter-log"
                    />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-ux-text-3 mb-1">
                        Model id
                    </div>
                    <input
                        value={modelFilter}
                        onChange={(e) => setModelFilter(e.target.value)}
                        placeholder="optional filter"
                        className="px-2 py-1.5 rounded-md bg-ux-bg-1 border border-ux-border-1 text-[13px] text-ux-text-1"
                        data-testid="calibration-filter-model"
                    />
                </div>
            </div>

            <div className="rounded-lg border border-ux-border-1 overflow-hidden">
                <table className="w-full text-[13px]" data-testid="calibration-table">
                    <thead className="bg-ux-bg-1 text-ux-text-3 text-[11px] uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-3 py-2">ID</th>
                            <th className="text-left px-3 py-2">Model</th>
                            <th className="text-left px-3 py-2">Log</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Before</th>
                            <th className="text-left px-3 py-2">After</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="px-3 py-8 text-center text-ux-text-3">
                                    loading…
                                </td>
                            </tr>
                        )}
                        {!loading && filtered.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-3 py-8 text-center text-ux-text-3">
                                    No sessions match.
                                </td>
                            </tr>
                        )}
                        {filtered.map((s) => {
                            const id = s.id || s.session_id;
                            return (
                                <tr
                                    key={id}
                                    onClick={() => openDetail(s)}
                                    className="border-t border-ux-border-1 hover:bg-ux-bg-1 cursor-pointer"
                                    data-testid={`calibration-row-${id}`}
                                >
                                    <td className="px-3 py-2 font-mono text-ux-text-2">
                                        {id?.slice(0, 10)}…
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-1">{s.model_id || "—"}</td>
                                    <td className="px-3 py-2 text-ux-text-2">
                                        {s.log_import_id || "—"}
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-1">{s.status || "—"}</td>
                                    <td className="px-3 py-2 text-ux-text-3 font-mono">
                                        {s.before_metric ?? s.before_metrics?.rmse ?? "—"}
                                    </td>
                                    <td className="px-3 py-2 text-ux-text-3 font-mono">
                                        {s.after_metric ?? s.after_metrics?.rmse ?? "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {open && (
                <div
                    className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setOpen(null);
                    }}
                >
                    <div
                        className="w-full max-w-3xl bg-ux-bg-1 rounded-lg border border-ux-border-1 max-h-[85vh] flex flex-col"
                        data-testid="calibration-detail-modal"
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-ux-border-1">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-ux-text-3">
                                    Calibration session
                                </div>
                                <div className="font-mono text-[14px] text-ux-text-1">
                                    {open.id || open.session_id}
                                </div>
                            </div>
                            <button
                                onClick={() => setOpen(null)}
                                className="text-ux-text-3 hover:text-ux-text-1"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-md border border-ux-border-1 p-3">
                                    <div className="text-[10px] uppercase tracking-wider text-ux-text-3 mb-1">
                                        Before metrics
                                    </div>
                                    <pre className="text-[11px] text-ux-text-2 font-mono whitespace-pre-wrap">
                                        {JSON.stringify(open.before_metrics || {}, null, 2)}
                                    </pre>
                                </div>
                                <div className="rounded-md border border-ux-border-1 p-3">
                                    <div className="text-[10px] uppercase tracking-wider text-ux-text-3 mb-1">
                                        After metrics
                                    </div>
                                    <pre className="text-[11px] text-ux-text-2 font-mono whitespace-pre-wrap">
                                        {JSON.stringify(open.after_metrics || {}, null, 2)}
                                    </pre>
                                </div>
                            </div>
                            <div className="rounded-md border border-ux-border-1 p-3">
                                <div className="text-[10px] uppercase tracking-wider text-ux-text-3 mb-1">
                                    Proposed parameter deltas
                                </div>
                                <pre className="text-[11px] text-ux-text-2 font-mono whitespace-pre-wrap">
                                    {JSON.stringify(open.proposed_parameter_deltas || open.deltas || {}, null, 2)}
                                </pre>
                            </div>
                        </div>
                        {isAdmin && open.status === "proposed" && (
                            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-ux-border-1">
                                <button
                                    onClick={() => decide(open.id || open.session_id, "reject")}
                                    className="px-3 py-1.5 rounded-md border border-ux-danger/40 text-ux-danger hover:bg-ux-danger/10 text-[13px]"
                                    data-testid="calibration-reject-button"
                                >
                                    Reject
                                </button>
                                <button
                                    onClick={() => decide(open.id || open.session_id, "accept")}
                                    className="px-3 py-1.5 rounded-md bg-ux-accent text-black text-[13px]"
                                    data-testid="calibration-accept-button"
                                >
                                    Accept
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
